// @flow
import React from 'react';
import glamorous from 'glamorous';
import styled from '@emotion/styled';

import MatchUpLine from '../../components/challenge/MatchUpLine';
import PlayerAvatar from '../../components/challenge/PlayerAvatar';
import PlayerLabels from '../../components/challenge/PlayerLabels';
import WidgetEntityRowRapidFire from '../../components/challenge/WidgetEntityRowRapidFire';
import Order from '../../components/commons/Order';
import Icon from '../../components/commons/Icon';
import OneScoreWidgetRapidFire from '../../widgets/one-sport/OneScoreWidgetRapidFire';

const Player = ({
  competitionId,
  player,
  onSelect,
  reverseLayout,
  selected,
  showWidget,
  toggleWidget,
  hideMatchUpLine,
  isCompletedView = false
}: Props) => (
  <Wrapper>
    <PlayerRowWrapper reverseLayout={reverseLayout}>
      <Order desktop={2} mobile={1}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div onClick={toggleWidget} onKeyDown={toggleWidget}>
          <PlayerAvatar
            size="large"
            fallbackText={player.initials}
            image={player.imageURI}
            reverseLayout={reverseLayout}
            isCompletedView={isCompletedView}
            showStatsIcon
          />
        </div>
      </Order>
      <Order desktop={3} mobile={2}>
        <Stack reverseLayout={reverseLayout}>
          <Name>{player.name}</Name>
          <PlayerRow>
            {player.number !== undefined && (
              <PlayerJersey>
                <Icon type="icon-jersey" />
                <span style={{ paddingLeft: '0.1em' }}>{player.number}</span>
              </PlayerJersey>
            )}
            {player.labels && player.labels.length > 0 && <PlayerLabels items={player.labels} />}
          </PlayerRow>
          <Stats>
            Avg {player.average} | Form {player.form}
          </Stats>
          <Stats>
            {!hideMatchUpLine && (
              <MatchUpLine
                awayTeam={player.awayAbbr}
                homeTeam={player.homeAbbr}
                highlightAway={!player.isHome}
                highlightHome={player.isHome}
                reverseOrder={player.reverseOrder}
              />
            )}
          </Stats>
        </Stack>
      </Order>
      <Order desktop={1} mobile={3}>
        <SelectBox
          selected={selected}
          onClick={() => {
            onSelect(player.id);
          }}
        />
      </Order>
    </PlayerRowWrapper>

    <OneScoreWidgetRapidFire
      selectedPlayer={player}
      competitionId={competitionId}
      isRacing={false}
      isOpen={showWidget}
      onClose={() => toggleWidget(player.idStr)}
      renderEntity={() => (
        <WidgetEntityRowRapidFire
          player={player}
          competitionId={competitionId}
          idStr={player.idStr}
          isTeam={player.isTeam}
          onSelect={() => {
            onSelect();
            toggleWidget(player.idStr);
          }}
          onRemove={() => toggleWidget(player.idStr)}
          selected={selected}
        />
      )}
    />
  </Wrapper>
);

export default Player;

interface Props {
  competitionId: string;
  hideMatchUpLine?: boolean;
  player: Object;
  reverseLayout: boolean;
  selected: boolean;
  isCompletedView?: boolean;
  onSelect: () => void;
  toggleWidget: () => void;
  showWidget: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PlayerRowWrapper = glamorous.div(
  {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    backgroundColor: 'white'
  },
  ({ theme, reverseLayout }) => ({
    '& > *': {
      marginLeft: reverseLayout ? theme.spacing.small : 0,
      marginRight: reverseLayout ? 0 : theme.spacing.small
    },
    padding: theme.spacing.small,
    fontSize: theme.fontSize.small,
    [theme.mediaQuery.desktopAndUp]: {
      flexDirection: reverseLayout ? 'row' : 'row-reverse'
    }
  })
);

const Name = glamorous.span(({ theme }) => ({
  fontSize: theme.fontSize.default,
  textAlign: 'center'
}));

const Stats = glamorous.span({});

const TICK = '"\\f3fd"';

const SelectBox = glamorous.button(
  ({ theme }) => ({
    outline: 'none',
    borderRadius: '100%',
    width: 40,
    height: 40,
    color: theme.color.white,
    fontWeight: theme.fontWeight.bold
  }),
  ({ selected, theme }) =>
    selected
      ? {
          backgroundColor: theme.color.green,
          '&::after': {
            fontSize: '1.7rem',
            alignItems: 'center',
            display: 'flex',
            content: TICK,
            fontFamily: 'Ionicons',
            justifyContent: 'center'
          }
        }
      : {
          border: '2px solid',
          borderColor: theme.color.basis4
        }
);

const Stack = glamorous.div(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  ({ reverseLayout, theme }) => ({
    [theme.mediaQuery.desktopAndUp]: {
      alignItems: reverseLayout ? 'flex-start' : 'flex-end'
    }
  })
);

const PlayerRow = styled.div({
  display: 'flex',
  marginTop: '1px',
  marginBottom: '1px',
  lineHeight: 1
});

const PlayerJersey = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});
